"use client";

import { c as _c } from "react/compiler-runtime";
import { HelpCircle, InfoCircle } from "@untitledui/icons";
import { type ComponentType, type HTMLAttributes, type ReactNode, type Ref, createContext, use } from "react";
import type { InputProps as AriaInputProps, TextFieldProps as AriaTextFieldProps } from "react-aria-components";
import { Group as AriaGroup, Input as AriaInput, TextField as AriaTextField } from "react-aria-components";
import { HintText } from "@/components/base/input/hint-text";
import { Label } from "@/components/base/input/label";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { cx, sortCx } from "@/utils/cx";
export interface InputBaseProps extends TextFieldProps {
  /** Tooltip message on hover. */
  tooltip?: string;
  /**
   * Input size.
   * @default "sm"
   */
  size?: "sm" | "md" | "lg";
  /** Placeholder text. */
  placeholder?: string;
  /** Class name for the icon. */
  iconClassName?: string;
  /** Class name for the input. */
  inputClassName?: string;
  /** Class name for the input wrapper. */
  wrapperClassName?: string;
  /** Class name for the tooltip. */
  tooltipClassName?: string;
  /** Keyboard shortcut to display. */
  shortcut?: string | boolean;
  ref?: Ref<HTMLInputElement>;
  groupRef?: Ref<HTMLDivElement>;
  /** Icon component to display on the left side of the input. */
  icon?: ComponentType<HTMLAttributes<HTMLOrSVGElement>>;
}
export const InputBase = t0 => {
  const $ = _c(42);
  let Icon;
  let groupRef;
  let iconClassName;
  let inputClassName;
  let inputProps;
  let isDisabled;
  let isInvalid;
  let placeholder;
  let ref;
  let shortcut;
  let t1;
  let tooltip;
  let tooltipClassName;
  let wrapperClassName;
  if ($[0] !== t0) {
    const {
      ref: t2,
      tooltip: t3,
      shortcut: t4,
      groupRef: t5,
      size: t6,
      isInvalid: t7,
      isDisabled: t8,
      icon: t9,
      placeholder: t10,
      wrapperClassName: t11,
      tooltipClassName: t12,
      inputClassName: t13,
      iconClassName: t14,
      isRequired: _isRequired,
      ...t15
    } = t0;
    ref = t2;
    tooltip = t3;
    shortcut = t4;
    groupRef = t5;
    t1 = t6;
    isInvalid = t7;
    isDisabled = t8;
    Icon = t9;
    placeholder = t10;
    wrapperClassName = t11;
    tooltipClassName = t12;
    inputClassName = t13;
    iconClassName = t14;
    inputProps = t15;
    $[0] = t0;
    $[1] = Icon;
    $[2] = groupRef;
    $[3] = iconClassName;
    $[4] = inputClassName;
    $[5] = inputProps;
    $[6] = isDisabled;
    $[7] = isInvalid;
    $[8] = placeholder;
    $[9] = ref;
    $[10] = shortcut;
    $[11] = t1;
    $[12] = tooltip;
    $[13] = tooltipClassName;
    $[14] = wrapperClassName;
  } else {
    Icon = $[1];
    groupRef = $[2];
    iconClassName = $[3];
    inputClassName = $[4];
    inputProps = $[5];
    isDisabled = $[6];
    isInvalid = $[7];
    placeholder = $[8];
    ref = $[9];
    shortcut = $[10];
    t1 = $[11];
    tooltip = $[12];
    tooltipClassName = $[13];
    wrapperClassName = $[14];
  }
  const size = t1 === undefined ? "sm" : t1;
  const hasTrailingIcon = tooltip || isInvalid;
  const hasLeadingIcon = Icon;
  const context = use(TextFieldContext);
  const inputSize = context?.size || size;
  let t2;
  if ($[15] !== Icon || $[16] !== context?.iconClassName || $[17] !== context?.inputClassName || $[18] !== context?.tooltipClassName || $[19] !== context?.wrapperClassName || $[20] !== groupRef || $[21] !== hasLeadingIcon || $[22] !== hasTrailingIcon || $[23] !== iconClassName || $[24] !== inputClassName || $[25] !== inputProps || $[26] !== inputSize || $[27] !== isDisabled || $[28] !== isInvalid || $[29] !== placeholder || $[30] !== ref || $[31] !== shortcut || $[32] !== tooltip || $[33] !== tooltipClassName || $[34] !== wrapperClassName) {
    const sizes = sortCx({
      sm: {
        root: cx("px-3 py-2", hasTrailingIcon && "pr-9", hasLeadingIcon && "pl-10"),
        iconLeading: "left-3",
        iconTrailing: "right-3",
        shortcut: "pr-2.5"
      },
      md: {
        root: cx("px-3.5 py-2.5", hasTrailingIcon && "pr-9.5", hasLeadingIcon && "pl-10.5"),
        iconLeading: "left-3.5",
        iconTrailing: "right-3.5",
        shortcut: "pr-3"
      },
      lg: {
        root: cx("px-4 py-4", hasTrailingIcon && "pr-9.5", hasLeadingIcon && "pl-10.5"),
        iconLeading: "left-4",
        iconTrailing: "right-4",
        shortcut: "pr-3.5"
      }
    });
    let t3;
    if ($[36] !== isDisabled || $[37] !== isInvalid) {
      t3 = {
        isDisabled,
        isInvalid
      };
      $[36] = isDisabled;
      $[37] = isInvalid;
      $[38] = t3;
    } else {
      t3 = $[38];
    }
    let t4;
    if ($[39] !== context?.wrapperClassName || $[40] !== wrapperClassName) {
      t4 = t5 => {
        const {
          isFocusWithin,
          isDisabled: isDisabled_0,
          isInvalid: isInvalid_0
        } = t5;
        return cx("relative flex w-full flex-row place-content-center place-items-center rounded-lg bg-primary shadow-xs ring-1 ring-primary transition-shadow duration-100 ease-linear ring-inset", isFocusWithin && !isDisabled_0 && "ring-2 ring-brand", isDisabled_0 && "cursor-not-allowed bg-disabled_subtle ring-disabled", "group-disabled:cursor-not-allowed group-disabled:bg-disabled_subtle group-disabled:ring-disabled", isInvalid_0 && "ring-error_subtle", "group-invalid:ring-error_subtle", isInvalid_0 && isFocusWithin && "ring-2 ring-error", isFocusWithin && "group-invalid:ring-2 group-invalid:ring-error", context?.wrapperClassName, wrapperClassName);
      };
      $[39] = context?.wrapperClassName;
      $[40] = wrapperClassName;
      $[41] = t4;
    } else {
      t4 = $[41];
    }
    t2 = <AriaGroup {...t3} ref={groupRef} className={t4}>{Icon && <Icon className={cx("pointer-events-none absolute size-5 text-fg-quaternary", isDisabled && "text-fg-disabled", sizes[inputSize].iconLeading, context?.iconClassName, iconClassName)} />}<AriaInput {...inputProps as AriaInputProps} ref={ref} placeholder={placeholder} className={cx("m-0 w-full bg-transparent text-md text-primary ring-0 outline-hidden placeholder:text-placeholder autofill:rounded-lg autofill:text-primary", isDisabled && "cursor-not-allowed text-disabled", sizes[inputSize].root, context?.inputClassName, inputClassName)} />{tooltip && !isInvalid && <Tooltip title={tooltip} placement="top"><TooltipTrigger aria-label={tooltip} className={cx("absolute cursor-pointer text-fg-quaternary transition duration-200 hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover", sizes[inputSize].iconTrailing, context?.tooltipClassName, tooltipClassName)}><HelpCircle className="size-4" /></TooltipTrigger></Tooltip>}{isInvalid && <InfoCircle className={cx("pointer-events-none absolute size-4 text-fg-error-secondary", sizes[inputSize].iconTrailing, context?.tooltipClassName, tooltipClassName)} />}{shortcut && <div className={cx("pointer-events-none absolute inset-y-0.5 right-0.5 z-10 flex items-center rounded-r-[inherit] bg-linear-to-r from-transparent to-bg-primary to-40% pl-8", sizes[inputSize].shortcut)}><span className={cx("pointer-events-none rounded px-1 py-px text-xs font-medium text-quaternary ring-1 ring-secondary select-none ring-inset", isDisabled && "bg-transparent text-disabled")} aria-hidden="true">{typeof shortcut === "string" ? shortcut : "\u2318K"}</span></div>}</AriaGroup>;
    $[15] = Icon;
    $[16] = context?.iconClassName;
    $[17] = context?.inputClassName;
    $[18] = context?.tooltipClassName;
    $[19] = context?.wrapperClassName;
    $[20] = groupRef;
    $[21] = hasLeadingIcon;
    $[22] = hasTrailingIcon;
    $[23] = iconClassName;
    $[24] = inputClassName;
    $[25] = inputProps;
    $[26] = inputSize;
    $[27] = isDisabled;
    $[28] = isInvalid;
    $[29] = placeholder;
    $[30] = ref;
    $[31] = shortcut;
    $[32] = tooltip;
    $[33] = tooltipClassName;
    $[34] = wrapperClassName;
    $[35] = t2;
  } else {
    t2 = $[35];
  }
  return t2;
};
InputBase.displayName = "InputBase";
interface BaseProps {
  /** Label text for the input */
  label?: string;
  /** Helper text displayed below the input */
  hint?: ReactNode;
}
interface TextFieldProps extends BaseProps, AriaTextFieldProps, Pick<InputBaseProps, "size" | "wrapperClassName" | "inputClassName" | "iconClassName" | "tooltipClassName"> {
  ref?: Ref<HTMLDivElement>;
}
const TextFieldContext = createContext<TextFieldProps>({});
export const TextField = t0 => {
  const $ = _c(11);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = state => cx("group flex h-max w-full flex-col items-start justify-start gap-1.5", typeof className === "function" ? className(state) : className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <AriaTextField {...props} data-input-wrapper={true} className={t1} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== props || $[9] !== t2) {
    t3 = <TextFieldContext.Provider value={props}>{t2}</TextFieldContext.Provider>;
    $[8] = props;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};
TextField.displayName = "TextField";
interface InputProps extends InputBaseProps, BaseProps {
  /** Whether to hide required indicator from label */
  hideRequiredIndicator?: boolean;
}
export const Input = t0 => {
  const $ = _c(39);
  let Icon;
  let className;
  let groupRef;
  let hideRequiredIndicator;
  let hint;
  let iconClassName;
  let inputClassName;
  let label;
  let onPaste;
  let placeholder;
  let props;
  let ref;
  let shortcut;
  let t1;
  let tooltip;
  let tooltipClassName;
  let wrapperClassName;
  if ($[0] !== t0) {
    ({
      size: t1,
      placeholder,
      icon: Icon,
      label,
      hint,
      shortcut,
      hideRequiredIndicator,
      className,
      ref,
      groupRef,
      tooltip,
      iconClassName,
      inputClassName,
      wrapperClassName,
      tooltipClassName,
      onPaste,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = Icon;
    $[2] = className;
    $[3] = groupRef;
    $[4] = hideRequiredIndicator;
    $[5] = hint;
    $[6] = iconClassName;
    $[7] = inputClassName;
    $[8] = label;
    $[9] = onPaste;
    $[10] = placeholder;
    $[11] = props;
    $[12] = ref;
    $[13] = shortcut;
    $[14] = t1;
    $[15] = tooltip;
    $[16] = tooltipClassName;
    $[17] = wrapperClassName;
  } else {
    Icon = $[1];
    className = $[2];
    groupRef = $[3];
    hideRequiredIndicator = $[4];
    hint = $[5];
    iconClassName = $[6];
    inputClassName = $[7];
    label = $[8];
    onPaste = $[9];
    placeholder = $[10];
    props = $[11];
    ref = $[12];
    shortcut = $[13];
    t1 = $[14];
    tooltip = $[15];
    tooltipClassName = $[16];
    wrapperClassName = $[17];
  }
  const size = t1 === undefined ? "sm" : t1;
  const t2 = !label ? placeholder : undefined;
  let t3;
  if ($[18] !== Icon || $[19] !== groupRef || $[20] !== hideRequiredIndicator || $[21] !== hint || $[22] !== iconClassName || $[23] !== inputClassName || $[24] !== label || $[25] !== onPaste || $[26] !== placeholder || $[27] !== ref || $[28] !== shortcut || $[29] !== size || $[30] !== tooltip || $[31] !== tooltipClassName || $[32] !== wrapperClassName) {
    t3 = t4 => {
      const {
        isRequired,
        isInvalid
      } = t4;
      return <>{label && <Label isRequired={hideRequiredIndicator ? !hideRequiredIndicator : isRequired} tooltip={tooltip}>{label}</Label>}<InputBase {...{
          ref,
          groupRef,
          size,
          placeholder,
          icon: Icon,
          shortcut,
          iconClassName,
          inputClassName,
          wrapperClassName,
          tooltipClassName,
          tooltip: label ? undefined : tooltip,
          onPaste
        }} />{hint && <HintText isInvalid={isInvalid}>{hint}</HintText>}</>;
    };
    $[18] = Icon;
    $[19] = groupRef;
    $[20] = hideRequiredIndicator;
    $[21] = hint;
    $[22] = iconClassName;
    $[23] = inputClassName;
    $[24] = label;
    $[25] = onPaste;
    $[26] = placeholder;
    $[27] = ref;
    $[28] = shortcut;
    $[29] = size;
    $[30] = tooltip;
    $[31] = tooltipClassName;
    $[32] = wrapperClassName;
    $[33] = t3;
  } else {
    t3 = $[33];
  }
  let t4;
  if ($[34] !== className || $[35] !== props || $[36] !== t2 || $[37] !== t3) {
    t4 = <TextField aria-label={t2} {...props} className={className}>{t3}</TextField>;
    $[34] = className;
    $[35] = props;
    $[36] = t2;
    $[37] = t3;
    $[38] = t4;
  } else {
    t4 = $[38];
  }
  return t4;
};
Input.displayName = "Input";